import type { ReactNode } from 'react';

import { Outlet } from 'react-router-dom';

import type { AppSectionKey } from '@/shared/navigation/appSections';
import { useSectionAccess } from '@/shared/navigation/useSectionAccess';

import { AccessRefusal } from './AccessRefusal';

interface RequireSectionProps {
  /** The registry key of the area behind this guard. */
  section: AppSectionKey;
  /** Rendered when permitted. Omit to render the nested `<Outlet />` (route-element usage). */
  children?: ReactNode;
}

/**
 * Route guard for a registered application section.
 *
 * <p>Prefer this over {@link RequirePermission}: naming the *section* rather than a permission code
 * means the route and the sidebar entry cannot disagree, because both resolve the requirement from
 * {@code APP_SECTIONS}. Hard-coding a code at the route is how `/loyalty` came to advertise a screen
 * that refused the very users the sidebar invited.
 *
 * <p><b>Feature flags are deliberately NOT part of this decision.</b> A flag hides a section from the
 * menu; it never refuses its URL. {@link isSectionPermitted} — which this guard resolves through —
 * ignores flags on purpose, and {@code appSections.test.ts} pins that. Answering a deep link with
 * "Access restricted" to a user who holds the permission is simply untrue, and it breaks the pilot
 * links that staging exists to support: a right is a right; a rollout schedule is not
 * (adr-auth-006 D6). This paragraph previously claimed the opposite and was stale.
 *
 * <p>This is not the security boundary — the BFF enforces the same permission on every call. It is what
 * stops the portal offering, or half-rendering, work the user may not do.
 *
 * <p>Lives in `shared/` rather than `app/router/` because every feature's own route table needs it, and
 * a feature reaching up into the composition root is the dependency direction Feature-Sliced Design
 * exists to prevent. {@code RequireAuth} correctly stays in `app/router/`: it gates the shell mount
 * itself and nothing outside the app layer composes it.
 */
export function RequireSection({ section, children }: RequireSectionProps) {
  const { canEnter } = useSectionAccess();

  if (!canEnter(section)) {
    return <AccessRefusal />;
  }

  return <>{children ?? <Outlet />}</>;
}
